// src/utils/imageVariants.js — v0.101.0 (2026-07-22), v0.171.0 (2026-09-14)
// ---------------------------------------------------------------------------
// Downscaled copies of a captured photo, made in the BROWSER.
//
// WHY THIS EXISTS
//   DefectPhotos has always had a ThumbnailUrl column that nothing populated,
//   so every consumer wanting a small image fell back to the full-size original.
//   Verified live on 2026-07-22: report 20, 21 photos, 21 with no thumbnail.
//   Two things broke as a result —
//     • the InSpec Mail Room preview: inlining originals blew the size cap, so
//       tightening it to something sane skipped every photo and enquiries
//       arrived with none at all;
//     • the PDF report: it embeds originals (3-6 MB each from a phone), so a
//       14-defect report ran to tens of MB and could not be emailed.
//
// WHY IN THE BROWSER, NOT THE API
//   DropPin's functions run inside a Static Web App. sharp/jimp ship native
//   binaries that are awkward-to-impossible to deploy there, and the phone
//   already holds the decoded image. Canvas costs us nothing and keeps the
//   Functions app dependency-free.
//
// v0.171.0 — PHOTOS AS BYTES, AND A SANE "ORIGINAL".
//   makePhotoBlobs(file) is the capture path now. It takes the File straight
//   from the camera / picker (no FileReader → base64 string first), decodes it
//   ONCE, and returns Blobs: the stored original, the display copy and the
//   thumbnail. Blobs go into the outbox as bytes (see outbox.js) and only
//   become base64 at the moment of sending.
//
//   THE ORIGINAL IS CAPPED at ORIGINAL_MAX_EDGE on its long edge, re-encoded as
//   JPEG. A photo already within the cap is stored byte-for-byte as it came in.
//   The old makeImageVariants(dataUrl) stays for the report screen's backfill
//   of pre-v0.101 photos.
//
// v0.194.0 — THE CAP WAS COSTING DETAIL THE VIEWER NEEDED.
//   Brett, 2026-09-15: "If I want to look at the photo we open in a viewer.
//   The viewer should have best quality."
//
//   He is right, and the viewer was never the problem — PhotoLightbox already
//   requests photo.url, which is the stored ORIGINAL, and /api/defect-photos
//   and /api/public-report both serve DefectPhotos.Url. The detail was being
//   destroyed on the phone, before upload, in this file:
//
//     • a 12 MP iPhone frame (4032×3024) was downscaled to 2560 px — about
//       60% of the pixels gone;
//     • then re-encoded at JPEG 0.85 on top of the camera's own compression;
//     • and on an iPhone shooting HEIC, that was the SECOND lossy generation
//       (heicPhotos.js had already converted at 0.9).
//
//   Pinch-zooming a hairline crack in a tile was zooming into a twice-cooked
//   2560 px JPEG. No viewer change could recover that.
//
//   The 2560 cap was chosen for ONE reason: upload size over a site link. That
//   reason is gone — v0.171.0 site mode holds everything on the phone and
//   pushes on Wi-Fi, which is exactly the case the cap was defending against.
//
//   So: ORIGINAL_MAX_EDGE is now 4096, which is ABOVE a 12 MP phone frame's
//   long edge. scaleToBlob() returns null when the image is already within the
//   cap, so the effect is not "a bigger re-encode" — it is NO RE-ENCODE AT ALL.
//   The camera's own JPEG is stored byte-for-byte. Zero generational loss.
//   Quality 0.92 now applies only to the genuinely huge frames (48 MP Pro
//   modes, DSLRs) that still need bringing down.
//
//   DISPLAY and THUMB are deliberately UNCHANGED. The PDF reads displayUrl at
//   1600 px and list rows read thumbnailUrl at 400 px, so report size, email
//   deliverability and list scroll speed are all untouched. Only the copy you
//   open and zoom got better.
//
//   COST OF THIS: a stored photo goes from ~0.8–1.5 MB to ~2.5–4 MB. Blob
//   storage is trivial (a few cents a month). The real cost is the phone: in
//   site mode a day's queue of photos sits in IndexedDB at roughly 3× its old
//   size before it is pushed. persistStorage.js's advice banner is what warns
//   about that.
//
// WHAT IT DOES NOT DO
//   Every consumer still falls back to Url when a variant is missing, so a
//   photo whose downscale failed keeps working exactly as it does today.

// Long-edge targets. THUMB is for list rows, pin previews and the enquiry
// inlines; DISPLAY is for the PDF and any full-page view that does not need
// camera resolution; ORIGINAL is what is stored as the source of truth.
export const THUMB_MAX_EDGE    = 400;
export const DISPLAY_MAX_EDGE  = 1600;
export const ORIGINAL_MAX_EDGE = 4096;
const THUMB_QUALITY    = 0.7;
const DISPLAY_QUALITY  = 0.8;
const ORIGINAL_QUALITY = 0.92;

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload  = () => resolve(img);
    img.onerror = () => reject(new Error('image decode failed'));
    img.src = src;
  });
}

// Decode a File/Blob once. createImageBitmap honours EXIF orientation where
// the browser supports the option, so a portrait phone shot stays portrait;
// the <img> route is the fallback (modern browsers auto-orient there too).
async function decodeBlob(blob) {
  if (typeof createImageBitmap === 'function') {
    try {
      return await createImageBitmap(blob, { imageOrientation: 'from-image' });
    } catch (_e) { /* fall through */ }
  }
  const url = URL.createObjectURL(blob);
  try { return await loadImage(url); }
  finally { try { URL.revokeObjectURL(url); } catch (_e) { /* fine */ } }
}

function dims(img) {
  return {
    w: img.naturalWidth || img.width || 0,
    h: img.naturalHeight || img.height || 0,
  };
}

function scaledCanvas(img, maxEdge) {
  const { w, h } = dims(img);
  if (!w || !h) return null;
  const longEdge = Math.max(w, h);
  if (longEdge <= maxEdge) return null;      // already small enough
  const scale = maxEdge / longEdge;
  const cw = Math.max(1, Math.round(w * scale));
  const ch = Math.max(1, Math.round(h * scale));
  const canvas = document.createElement('canvas');
  canvas.width = cw;
  canvas.height = ch;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  // Better downsampling than the default on large ratios.
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(img, 0, 0, cw, ch);
  return canvas;
}

// Draw `img` onto a canvas scaled so its LONG edge is at most maxEdge, and
// return a JPEG data URL. Returns null if the image is already smaller — the
// caller then reuses the original rather than storing a pointless copy.
function scaleToDataUrl(img, maxEdge, quality) {
  const canvas = scaledCanvas(img, maxEdge);
  // Always JPEG: a downscaled photo has no reason to be a PNG, and PNG would
  // often come out LARGER than the original it is meant to shrink.
  return canvas ? canvas.toDataURL('image/jpeg', quality) : null;
}

function canvasToBlob(canvas, quality) {
  return new Promise((resolve, reject) => {
    try {
      canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('toBlob produced nothing'))), 'image/jpeg', quality);
    } catch (e) { reject(e); }
  });
}

async function scaleToBlob(img, maxEdge, quality) {
  const canvas = scaledCanvas(img, maxEdge);
  return canvas ? await canvasToBlob(canvas, quality) : null;
}

// Build { thumbnailUrl, displayUrl } for a photo held as a data URL.
//
// FAIL-SOFT BY DESIGN: a photo that cannot be downscaled must still upload.
// Losing the client's site photo because a canvas call failed would be far
// worse than storing one image at full size, so every failure path returns
// nulls and lets the caller carry on with the original alone.
export async function makeImageVariants(dataUrl) {
  const empty = { thumbnailUrl: null, displayUrl: null };
  try {
    if (typeof document === 'undefined') return empty;      // non-browser context
    const src = String(dataUrl || '');
    if (!src) return empty;
    // Only raster images. A PDF page already arrives as a PNG data URL from
    // pdfPageToPng, so that path is covered; anything else is left alone.
    if (src.startsWith('data:') && !/^data:image\//i.test(src)) return empty;
    const img = await loadImage(src);
    return {
      thumbnailUrl: scaleToDataUrl(img, THUMB_MAX_EDGE,   THUMB_QUALITY),
      displayUrl:   scaleToDataUrl(img, DISPLAY_MAX_EDGE, DISPLAY_QUALITY),
    };
  } catch (_e) {
    return empty;
  }
}

/**
 * v0.171.0 — the capture path.
 *
 * makePhotoBlobs(file) → {
 *   original:  Blob   what is stored as Url (capped at ORIGINAL_MAX_EDGE, or
 *                     the untouched input when already within it)
 *   display:   Blob|null
 *   thumbnail: Blob|null
 *   width, height     of the stored original (null when unknown)
 * }
 *
 * FAIL-SOFT: if decoding fails entirely the input file is returned as the
 * original with no variants — the photo still uploads, at whatever size it
 * came in. Nothing here ever throws away a site photo.
 */
export async function makePhotoBlobs(file) {
  const fallback = { original: file, display: null, thumbnail: null, width: null, height: null };
  try {
    if (typeof document === 'undefined' || !file) return fallback;
    if (file.type && !/^image\//i.test(file.type)) return fallback;
    const img = await decodeBlob(file);
    try {
      const { w, h } = dims(img);
      const capped = await scaleToBlob(img, ORIGINAL_MAX_EDGE, ORIGINAL_QUALITY);
      const original = capped || file;
      let width = w, height = h;
      if (capped) {
        const scale = ORIGINAL_MAX_EDGE / Math.max(w, h);
        width = Math.max(1, Math.round(w * scale));
        height = Math.max(1, Math.round(h * scale));
      }
      const display   = await scaleToBlob(img, DISPLAY_MAX_EDGE, DISPLAY_QUALITY).catch(() => null);
      const thumbnail = await scaleToBlob(img, THUMB_MAX_EDGE,   THUMB_QUALITY).catch(() => null);
      return { original, display, thumbnail, width: width || null, height: height || null };
    } finally {
      if (img && typeof img.close === 'function') { try { img.close(); } catch (_e) { /* fine */ } }
    }
  } catch (_e) {
    return fallback;
  }
}
